import { useRealtimeSession } from "./hooks/useRealtimeSession";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import Pages from "./pages/";
import { Login } from "./pages/login.jsx";
import { ContextInit } from "./Context";
import axios from "axios";
import Authenticate from "./components/Authenticate";
import { Navbar } from "./components/modules";
import config from "./constants/configs";
import { db } from "./util/firebase";
import { flatten } from "ramda";
import { Toaster, useToast, Spinner } from "./components/ui";
import GlobalAdminAlerts from "./components/GlobalAdminAlerts";
import AdminPresenceTracker from "./components/AdminPresenceTracker";
import { sessionDecision, clearCurrentToken, isCurrentToken, newRequestId } from "./util/session";
import { executeApi } from "./util/apiHelper";
import { hasPagePermission } from "./util/permissionUtils";

function App() {
  const { toast } = useToast();
  React.useEffect(() => {
    window.alert = (msg, size = 'small') => {
      const className = size === 'large' ? 'bg-rose-600 text-white font-bold py-4 px-6 rounded-md text-lg' : 'bg-gray-800 text-white font-semibold py-2 px-3 rounded-md text-sm';
      toast({ title: msg, className });
    };
  }, []);

  const [configs, setConfigs] = useState();
  const [profileState, setProfileState] = useState();
  const [adminCart, setAdminCart] = useState([]);
  const [sessionError, setSessionError] = useState("");
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const sessionGeneration = useRef(0);
  const sessionVerification = useRef(null);
  const navigate = useNavigate();

  const hideModal = () => { };
  const setModalLoading = () => { };

  const callApi = async (path, body = {}, options = {}) => {
    const url = config.api;
    const requestToken = localStorage.getItem("accessToken");
    try {
      var data = JSON.stringify(body);
      var axiosConfigs = {
        timeout: 20000,
        method: body ? "post" : "get",
        url: url + path,
        headers: {
          "Content-Type": body?.append
            ? "application/x-www-form-urlencoded"
            : "application/json",
          accessToken: requestToken,
          "x-client-request-id": newRequestId(),
          action: path,
          ...configs,
          ...body?.configs,
        },
        data,
      };
      const response = await axios(axiosConfigs);

      // Ignore any response issued under a different account/session.
      if (!isCurrentToken(requestToken)) {
        if (requestToken) executeApi("logs-recordClientEvent", { eventType:"stale_response_ignored", requestId:response.headers?.["x-request-id"] });
        return false;
      }
      if ([401, 403].includes(response.data?.code) && !options.sessionCheck && !options.preserveSession) {
        // Verify in place: a permission error must not remount the page and repeat its request.
        let verificationRequest = sessionVerification.current;
        if (!verificationRequest || verificationRequest.token !== requestToken) {
          const promise = callApi("users-checkSupportSession", {}, { sessionCheck:true }).finally(() => {
            if (sessionVerification.current?.promise === promise) sessionVerification.current = null;
          });
          verificationRequest = {token:requestToken,promise};
          sessionVerification.current = verificationRequest;
        }
        const verification = await verificationRequest.promise;
        if (!isCurrentToken(requestToken)) return false;
        const decision = sessionDecision(verification);
        if (decision === "clear") {
          executeApi("logs-recordClientEvent", { eventType:"session_cleared", reasonCode:verification.reasonCode, requestId:verification.requestId });
          clearCurrentToken(requestToken);
          sessionGeneration.current++;
          setAdminCart([]);
          setProfileState({});
        } else if (decision === "retry" || decision === "forbidden") {
          setProfileState(undefined);
          setSessionError(decision === "forbidden" ? "บัญชีนี้ไม่มีสิทธิ์เข้า Support" : "ตรวจสอบการเข้าสู่ระบบไม่สำเร็จ กรุณาลองใหม่");
        } else if (response.data.toastMessage) window.alert(response.data.toastMessage);
        return false;
      }

      if (response.data.refresh) {
        window.location.reload();
      }
      if (response?.data?.toastMessage) {
        window.alert(response?.data?.toastMessage);
      }
      return options.sessionCheck ? { ...response.data, requestId: response.headers?.["x-request-id"] } : response.data;
    } catch (err) {
      // Do not print response bodies: authentication responses can contain credentials.
      return false;
    }
  };

  const realtime = useRealtimeSession(profileState?.admin ? profileState.userId : "", callApi);

  const promiseAllCallApi = async (callApis, options = {}) => {
    try {
      return await Promise.all(callApis);
    } catch (err) {
      return [];
    }
  };

  const updateConfig = async () => {
    const getConfigs = await callApi("configs-getConfig/", undefined, {
      notShowLoading: true,
      preserveSession: true,
    });
    if (!getConfigs || getConfigs.code >= 400) return null;
    const newConfigs = { ...getConfigs, ...config };
    setConfigs(newConfigs);
    return newConfigs;
  };

  useEffect(() => {
    let cancelled = false;
    const generation = ++sessionGeneration.current;
    const requestToken = localStorage.getItem("accessToken");
    const current = () => !cancelled && generation === sessionGeneration.current && isCurrentToken(requestToken);
    const restore = async () => {
      setSessionError("");
      if (!requestToken) {
        setProfileState({});
        await updateConfig();
        return;
      }
      setProfileState(undefined);
      const result = await callApi("users-checkSupportSession", {}, { sessionCheck: true });
      if (!current()) return;
      const decision = sessionDecision(result);
      if (decision === "clear") {
        // Telemetry is best effort; server session_rejected remains authoritative.
        executeApi("logs-recordClientEvent", { eventType: "session_cleared", reasonCode: result.reasonCode, requestId: result.requestId });
        clearCurrentToken(requestToken);
        setProfileState({});
        await updateConfig();
      } else if (decision === "valid") {
        const nextConfigs = await updateConfig();
        if (!current()) return;
        if (!nextConfigs) { setSessionError("โหลดการตั้งค่าไม่สำเร็จ กรุณาลองใหม่"); return; }
        setProfileState(result.data);
      } else {
        setSessionError(decision === "forbidden" ? "บัญชีนี้ไม่มีสิทธิ์เข้า Support กรุณาใช้บัญชีแอดมิน" : "ตรวจสอบการเข้าสู่ระบบไม่สำเร็จ กรุณาตรวจการเชื่อมต่อแล้วลองใหม่");
      }
    };
    restore();
    return () => { cancelled = true; };
  }, [sessionAttempt]);

  useEffect(() => {
    const sync = event => {
      if (event.key !== "accessToken" && event.key !== null) return;
      sessionGeneration.current++;
      setProfileState(undefined);
      setConfigs(undefined);
      setAdminCart([]);
      setSessionError("");
      setSessionAttempt(value => value + 1);
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  // Manage Dark Mode: Restricted exclusively to user ID 201989
  useEffect(() => {
    const isUser201989 = String(profileState?.userId) === "201989";
    const savedTheme = localStorage.getItem("wandee_theme_201989");
    if (isUser201989 && savedTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [profileState?.userId]);

  const token = localStorage.getItem("accessToken");
  const isAuthChecking = Boolean(token && profileState === undefined);
  const isLoggedIn = Boolean(
    token &&
    profileState &&
    (profileState.userId || profileState.admin || profileState.role || profileState._id || profileState.username)
  );

  // 1. Loading session verification screen
  if (isAuthChecking) {
    return (
      <div className="w-full h-dvh flex flex-col items-center justify-center gap-4 animate-in fade-in duration-300 select-none z-10">
        <img
          src="/logo.png"
          alt="วันดีลอตเตอรี่"
          className="w-24 h-auto object-contain drop-shadow-xs grayscale-[100%] opacity-10"
          onError={(e) => {
            e.target.src = "/images/HT_new_logo.png";
          }}
        />
        {!sessionError && <Spinner />}
        {sessionError && <div className="max-w-md px-4 text-center">
          <p role="alert" className="text-sm text-slate-700">{sessionError}</p>
          <button className="mt-4 rounded-md bg-navy px-4 py-2 text-white" onClick={() => setSessionAttempt(value => value + 1)}>ลองใหม่</button>
          <button className="ml-3 text-sm text-slate-600 underline" onClick={() => { executeApi("users-logout", {}); clearCurrentToken(token); setSessionAttempt(value => value + 1); }}>ใช้บัญชีอื่น</button>
        </div>}
        <p className="text-xs font-semibold text-slate-300/50 tracking-wide">
          {!sessionError && "กรุณารอสักครู่..."}
        </p>
      </div>
    );
  }

  // 2. Unauthenticated: Completely block sidebar and only allow /login
  if (!isLoggedIn) {
    return (
      <ContextInit.Provider
        value={{
          profileState: {},
          setProfileState,
          hideModal,
          callApi,
          configs,
          updateConfig,
          setConfigs,
          promiseAllCallApi,
          db,
          adminCart,
          setAdminCart,
          setModalLoading,
          navigate,
        }}
      >
        <Toaster />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ContextInit.Provider>
    );
  }

  if (!realtime.ready) return <div className="flex h-dvh flex-col items-center justify-center gap-3">
    {realtime.error ? <><p role="alert">เชื่อมต่อข้อมูลหลังบ้านไม่สำเร็จ</p><button onClick={realtime.retry}>ลองใหม่</button></> : <Spinner />}
  </div>;

  // 3. Authenticated: Render full admin shell with Navbar and authorized routes
  return (
    <ContextInit.Provider
      value={{
        profileState,
        setProfileState,
        hideModal,
        callApi,
        configs,
        updateConfig,
        setConfigs,
        promiseAllCallApi,
        db,
        adminCart,
        setAdminCart,
        setModalLoading,
        navigate,
      }}
    >
      <Toaster />
      <GlobalAdminAlerts profileState={profileState} />
      <AdminPresenceTracker profileState={profileState} />
      <div className="flex h-dvh bg-slate-100 dark:bg-[#1c2128] transition-colors duration-200">
        <Navbar />

        <div className="flex-1 min-w-0 overflow-y-auto overflow-x-auto p-2.5 sm:p-4 dark:bg-[#1c2128]">
          <Routes>
            <Route path="/login" element={<Navigate to="/" replace />} />
            {profileState &&
              configs &&
              flatten(
                Pages.filter((page) => hasPagePermission(page, profileState, configs)).map(
                  ({ component, path, children }) => {
                    const aPage = [
                      children
                        .filter((x) => hasPagePermission(x, profileState, configs))
                        .map(({ component, path }) => {
                          return (
                            <Route
                              key={path}
                              exact
                              path={path}
                              element={
                                <Authenticate path={path}>
                                  {component}
                                </Authenticate>
                              }
                            />
                          );
                        }),
                      component && (
                        <Route
                          key={path}
                          exact
                          path={path}
                          element={
                            <Authenticate path={path}>{component}</Authenticate>
                          }
                        />
                      ),
                    ];
                    return aPage;
                  },
                ),
              )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </ContextInit.Provider>
  );
}

export default App;
